import React, { useState, useEffect, useCallback } from "react";
import { Row, Col } from "antd";
import { useAuthState } from "/app/src/contexts/authentication";
import { useTranslation } from "react-i18next";
import { widgetService } from "/app/src/services";
import WidgetArea from "./WidgetArea";
import { Widget, View } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import HeroButton from "/app/src/components/HeroUi/Button";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";

export default function Widgets({
  view,
  editingView,
}: {
  view: View;
  editingView: boolean;
}) {
  const { t } = useTranslation();
  const [locked, setLocked] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const { isAuthenticated } = useAuthState();
  const queryClient = useQueryClient();

  // Update Widget in list

  //toggling the movement to on should set editing to false
  const toggleLocked = useCallback(() => {
    if (locked) {
      setIsEditing(false);
    }
    setLocked((prev) => !prev);
  }, [locked]);

  //toggling editing to on should set the movement to locked
  const toggleIsEditing = useCallback(() => {
    if (!isEditing) {
      setLocked(true);
    }
    setIsEditing((prev) => !prev);
  }, [isEditing]);

  // if the user toggles editing view to false,
  // ensure the widget editing is disabled and movement
  // is locked
  useEffect(() => {
    if (editingView === false) {
      setIsEditing(false);
      setLocked(true);
    }
  }, [editingView]);

  const { data: widgets } = useQuery({
    queryKey: ["widgets", view.id],
    queryFn: () => {
      return widgetService.getAll(buildParams({ viewId: view.id }));
    },
    select: (response: { widgets: Widget[] }) => response.widgets,
    initialData: { widgets: [] as Widget[] },
    enabled: Boolean(view.id),
  });

  const { mutateAsync: createWidget } = useMutation({
    mutationFn: (widget: Omit<Widget, "datasets">) => {
      return widgetService.createSingle(widget).then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["widgets", view.id],
        (oldData: { widgets: Widget[] }) => {
          return { widgets: [...oldData.widgets, response.widget] };
        },
      );
    },
  });

  const handleCreateClick = useCallback(() => {
    createWidget({ viewId: view.id });
  }, [createWidget, view.id]);

  return (
    <div className="dashboard" style={{ paddingTop: "15px" }}>
      {editingView && (
        <Row gutter={10} justify="end">
          <Col>
            <HeroButton color="primary" size="sm" onClick={handleCreateClick}>
              {t("translation:add_new_widget")}
            </HeroButton>
          </Col>
          <Col>
            <HeroButton
              size="sm"
              color={locked || isEditing ? "default" : "primary"}
              variant={locked || isEditing ? "bordered" : "solid"}
              className={
                (locked || isEditing) &&
                "bg-white hover:border-primary-default hover:text-primary-default"
              }
              onClick={toggleLocked}
            >
              {locked || isEditing ? (
                <>
                  <IconBuilder icon="Locked" size={14} />{" "}
                  {t("translation:edit_layout")}
                </>
              ) : (
                <>
                  <IconBuilder icon="Unlocked" color="#ffffff" size={14} />{" "}
                  {t("translation:finish_editing")}
                </>
              )}
            </HeroButton>
          </Col>
          <Col>
            <HeroButton
              size="sm"
              color={!isEditing ? "default" : "primary"}
              variant={!isEditing ? "bordered" : "solid"}
              className={
                !isEditing &&
                "bg-white hover:border-primary-default hover:text-primary-default"
              }
              onClick={toggleIsEditing}
            >
              {isEditing ? (
                <>
                  <IconBuilder icon="Unlocked" color="#ffffff" size={14} />{" "}
                  {t("translation:finish_editing")}
                </>
              ) : (
                <>
                  <IconBuilder icon="Locked" size={14} />{" "}
                  {t("translation:edit_widgets")}
                </>
              )}
            </HeroButton>
          </Col>
        </Row>
      )}
      {isAuthenticated && Boolean(widgets) ? (
        <WidgetArea widgets={widgets} isEditing={isEditing} locked={locked} />
      ) : null}
    </div>
  );
}
